/**
 * TranslationEditor — side-by-side modal to review + edit an AI
 * translation of the motivation letter before it's frozen as the
 * canonical download version for a target language.
 *
 * Loads {source_text, translated_text} from GET /letters/{lid}/translation/{lang}.
 * Saves the edited translation back with PUT to the same endpoint, which
 * writes it into `letter.letter_text_by_lang[lang]` so downloads pick it up.
 */
import { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

const LANG_LABEL = { nl: "Nederlands", en: "English", de: "Deutsch" };

export default function TranslationEditor({ letterId, targetLang, sourceLang, onClose, onSaved }) {
  const { t } = useT();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState("");
  const [translated, setTranslated] = useState("");
  const [wasCached, setWasCached] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/letters/${letterId}/translation/${targetLang}`);
        if (cancel) return;
        setSource(data.source_text || "");
        setTranslated(data.translated_text || "");
        setWasCached(!!data.cached);
      } catch (err) {
        if (!cancel) toast.error(err?.response?.data?.detail || t("trEdLoadFail"));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [letterId, targetLang, t]);

  const save = async () => {
    if (!translated.trim()) {
      toast.error(t("trEdEmpty"));
      return;
    }
    setSaving(true);
    try {
      await api.put(`/letters/${letterId}/translation/${targetLang}`, {
        translated_text: translated,
      });
      toast.success(t("trEdSaved"));
      onSaved?.(translated);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("trEdSaveFail"));
    } finally {
      setSaving(false);
    }
  };

  const srcLabel = LANG_LABEL[sourceLang] || sourceLang?.toUpperCase();
  const tgtLabel = LANG_LABEL[targetLang] || targetLang?.toUpperCase();
  const sameLang = sourceLang === targetLang;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="translation-editor"
    >
      <div
        className="bg-[#FAF7F1] w-full max-w-6xl max-h-[92vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-start justify-between gap-4 border-b border-neutral-300 px-6 py-4 shrink-0">
          <div>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500">
              {t("trEdEyebrow")}
            </p>
            <h2 className="font-serif text-2xl mt-1">
              {t("trEdTitle", { source: srcLabel, target: tgtLabel })}
            </h2>
            <p className="text-xs text-neutral-600 mt-2 max-w-xl">
              {sameLang
                ? t("trEdSameLangHint")
                : wasCached
                  ? t("trEdCachedHint")
                  : t("trEdFreshHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            data-testid="translation-editor-close"
            className="p-2 border border-neutral-300 hover:border-neutral-900"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        </header>

        {/* Panels */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center py-24 gap-2 text-neutral-600">
            <Loader2 size={16} className="animate-spin" />
            <span className="font-mono text-xs uppercase tracking-[0.15em]">{t("trEdLoading")}</span>
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-4 p-6 overflow-y-auto">
            {/* Source (read-only) */}
            <section className="flex flex-col min-h-0" data-testid="translation-editor-source">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2 flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 border border-neutral-400 bg-neutral-100">{srcLabel}</span>
                {t("trEdSourceLabel")}
              </p>
              <textarea
                readOnly
                value={source}
                data-testid="translation-editor-source-text"
                className="flex-1 min-h-[280px] resize-none font-serif text-sm leading-relaxed p-4 bg-white border border-neutral-300 text-neutral-800"
              />
            </section>

            {/* Target (editable) */}
            <section className="flex flex-col min-h-0" data-testid="translation-editor-target">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2 flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 border border-emerald-500 bg-emerald-50 text-emerald-800">{tgtLabel}</span>
                {t("trEdTargetLabel")}
              </p>
              <textarea
                value={translated}
                onChange={(e) => setTranslated(e.target.value)}
                data-testid="translation-editor-target-text"
                className="flex-1 min-h-[280px] resize-none font-serif text-sm leading-relaxed p-4 bg-white border border-neutral-900"
              />
            </section>
          </div>
        )}

        {/* Footer */}
        <footer className="border-t border-neutral-300 px-6 py-4 flex items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-neutral-500">
            {t("trEdFooterHint", { target: tgtLabel })}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-4 py-2"
              data-testid="translation-editor-cancel"
            >
              {t("trEdCancel")}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              data-testid="translation-editor-save"
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-40 px-4 py-2 inline-flex items-center gap-2"
            >
              <Save size={12} strokeWidth={1.75} /> {saving ? t("trEdSaving") : t("trEdSaveCta")}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
